import { useGetActiveUser } from "@/api";
import { TypographyMuted } from "@/components/typography/Muted";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  GroupingState,
  SortingState,
  useReactTable
} from "@tanstack/react-table";
import { isAfter, isBefore } from "date-fns";
import { AnimatePresence } from "framer-motion";
import { useState } from "react";
import { AppSessionsTableType, GlobalFilter } from "../types/appSession.type";
import { AppSessionFilters } from "./AppSessionFilters";
import { AppSessionsTableBody } from "./AppSessionsTableBody";
import { DownloadAnalyticsDataModal } from "./DownloadAnalyticsDataModal";

const ROW_RENDER_SECTION_COUNT = 60;

interface Props<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading: boolean;
}

export function AppSessionsTable<TData extends AppSessionsTableType, TValue>({
  columns,
  data,
  loading
}: Props<TData, TValue>) {
  const { data: userData } = useGetActiveUser();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [grouping, setGrouping] = useState<GroupingState>([]);
  const [globalFilter, setGlobalFilter] = useState<GlobalFilter>();
  const [limitRenderedRowsNumber, setLimitRenderedRowsNumber] = useState(
    ROW_RENDER_SECTION_COUNT
  );
  const [openDownloadModal, setOpenDownloadModal] = useState(false);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGroupingChange: setGrouping,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, _, value) => {
      if (!value) return true;

      const v = value as GlobalFilter;
      const { startTime, endTime, userId } = row.original;

      if (v.excludeMe && userData?.me.id && userData.me.id === userId)
        return false;

      const startTimeInRange = !v.startDate || isAfter(startTime, v.startDate);
      const endTimeInRange =
        !(v.endDate && endTime) || isBefore(endTime, v.endDate);
      return startTimeInRange && endTimeInRange;
    },
    state: {
      sorting,
      grouping,
      globalFilter
    }
  });

  const hasFilterApplied =
    globalFilter?.startDate || globalFilter?.endDate || globalFilter?.excludeMe;

  return (
    <>
      <div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setOpenDownloadModal(true)}
        >
          Download as CSV
        </Button>
        <div className="w-full border dark:bg-brand-gray-950 dark:border-brand-gray-900 flex justify-between items-center py-1.5 px-4 rounded gap-x-2 mt-2 mb-4">
          <div className="flex items-center gap-x-2">
            {loading && <LoadingCircle />}
            {hasFilterApplied && (
              <div className="flex items-center gap-x-2">
                <Badge variant="secondary" className="w-fit">
                  Filtered
                </Badge>
              </div>
            )}
            <TypographyMuted className="whitespace-nowrap">
              {hasFilterApplied && (
                <>Showing {table.getFilteredRowModel().rows.length} of </>
              )}
              {data.length} sessions
            </TypographyMuted>
          </div>
          <AppSessionFilters
            filters={globalFilter}
            setFilters={setGlobalFilter}
          />
        </div>
        <div
          className="rounded border max-h-[70vh] overflow-y-auto relative"
          onScroll={(e) => {
            if (limitRenderedRowsNumber > data.length) return;

            const { clientHeight, scrollHeight, scrollTop } =
              e.target as HTMLDivElement;
            const scrollBottomPosition = Math.ceil(scrollTop + clientHeight);

            if (scrollHeight - scrollBottomPosition < 300)
              setLimitRenderedRowsNumber(
                (prev) => prev + ROW_RENDER_SECTION_COUNT
              );
          }}
        >
          <Table className="overflow-y-auto relative">
            <TableHeader className="sticky top-0 z-10 bg-white dark:bg-brand-gray-950">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              <AppSessionsTableBody
                table={table}
                limitRenderedRowsNumber={limitRenderedRowsNumber}
              />
            </TableBody>
          </Table>
        </div>
      </div>
      <AnimatePresence>
        {openDownloadModal && (
          <DownloadAnalyticsDataModal
            table={table}
            onOpenChange={setOpenDownloadModal}
            hasFilteredData={!!globalFilter}
          />
        )}
      </AnimatePresence>
    </>
  );
}
